const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
const FEET_PER_METER = 3.28084;
const parseISODate = (s) => {
    if (!s || s.length < 10)
        return null;
    const year = parseInt(s.slice(0, 4), 10);
    const month = parseInt(s.slice(5, 7), 10) - 1;
    const day = parseInt(s.slice(8, 10), 10);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day))
        return null;
    if (month < 0 || month > 11)
        return null;
    return { year, month, day };
};
// ISO collection start/end → a short label ("May 2019", "May – Jul 2019") plus
// the full "May 25 – July 13, 2019" tooltip text. Null when no dates are known.
export const formatAcquisitionWindow = (startISO, endISO) => {
    const start = parseISODate(startISO);
    const end = parseISODate(endISO);
    if (start && !end) {
        return {
            short: `${MONTH_NAMES[start.month]} ${start.year}`,
            tooltip: `Collection started ${MONTH_NAMES[start.month]} ${start.day}, ${start.year}`,
        };
    }
    if (!start && end) {
        return {
            short: `${MONTH_NAMES[end.month]} ${end.year}`,
            tooltip: `Collection completed ${MONTH_NAMES[end.month]} ${end.day}, ${end.year}`,
        };
    }
    if (!start || !end)
        return null;
    const sameYear = start.year === end.year;
    const sameMonth = sameYear && start.month === end.month;
    const short = sameMonth
        ? `${MONTH_NAMES[start.month]} ${start.year}`
        : sameYear
            ? `${MONTH_NAMES[start.month].slice(0, 3)} – ${MONTH_NAMES[end.month].slice(0, 3)} ${start.year}`
            : `${MONTH_NAMES[start.month]} ${start.year} – ${MONTH_NAMES[end.month]} ${end.year}`;
    const tooltip = sameYear
        ? `Collected ${MONTH_NAMES[start.month]} ${start.day} – ${MONTH_NAMES[end.month]} ${end.day}, ${end.year}`
        : `Collected ${MONTH_NAMES[start.month]} ${start.day}, ${start.year} – ${MONTH_NAMES[end.month]} ${end.day}, ${end.year}`;
    return { short, tooltip };
};
const trimNumber = (v) => String(Number(v.toFixed(2)));
// The acquisition period as the credit states it: the collection window when
// known ("May – Jul 2019"), else the year or span of years ("2015–2020").
export const formatAcquisitionPeriod = (meta) => {
    const window = formatAcquisitionWindow(meta === null || meta === void 0 ? void 0 : meta.acquisitionStart, meta === null || meta === void 0 ? void 0 : meta.acquisitionEnd);
    if (window)
        return window.short;
    const end = meta === null || meta === void 0 ? void 0 : meta.acquisitionYear;
    if (!end)
        return null;
    const start = meta === null || meta === void 0 ? void 0 : meta.acquisitionYearStart;
    return start && start < end ? `${start}–${end}` : String(end);
};
// DEM resolution is a property of the data, always native metres. Like contour
// intervals, an imperial reader gets the translation in brackets.
export const formatResolution = (meters, system) => system === 'imperial'
    ? `${trimNumber(meters)} m (${(meters * FEET_PER_METER).toFixed(1)} ft)`
    : `${trimNumber(meters)} m`;
// The credit line a source asks for. System sources carry it in the registry;
// an uploaded DEM's is whatever licence the owner recorded, if any.
export const attributionStatement = (entry, meta) => {
    var _a, _b;
    if (!entry.isSystemSourced)
        return ((_a = meta === null || meta === void 0 ? void 0 : meta.license) === null || _a === void 0 ? void 0 : _a.trim()) || undefined;
    return (_b = entry.attribution) !== null && _b !== void 0 ? _b : entry.license;
};
export const SOURCE_CREDIT_DETAIL_LABELS = {
    project: 'Survey',
    workunit: 'Work unit',
    period: 'Acquired',
    resolution: 'Resolution',
    quality: 'Quality',
};
export const SOURCE_CREDIT_DETAIL_ORDER = ['project', 'workunit', 'period', 'resolution', 'quality'];
export const buildSourceCredit = (entry, meta, system) => {
    var _a, _b, _c, _d, _e, _f;
    const resolution = (_a = meta === null || meta === void 0 ? void 0 : meta.resolutionMeters) !== null && _a !== void 0 ? _a : entry.resolutionMeters;
    const projectName = ((_b = meta === null || meta === void 0 ? void 0 : meta.projectName) === null || _b === void 0 ? void 0 : _b.trim()) || undefined;
    const workunitName = ((_c = meta === null || meta === void 0 ? void 0 : meta.workunitName) === null || _c === void 0 ? void 0 : _c.trim()) || undefined;
    const details = {
        project: projectName !== null && projectName !== void 0 ? projectName : workunitName,
        workunit: projectName && workunitName !== projectName ? workunitName : undefined,
        period: (_d = formatAcquisitionPeriod(meta)) !== null && _d !== void 0 ? _d : undefined,
        resolution: resolution !== undefined ? formatResolution(resolution, system) : undefined,
        quality: ((_e = meta === null || meta === void 0 ? void 0 : meta.qualityLevel) === null || _e === void 0 ? void 0 : _e.trim()) || undefined,
    };
    return {
        dataset: ((_f = meta === null || meta === void 0 ? void 0 : meta.datasetName) === null || _f === void 0 ? void 0 : _f.trim()) || entry.datasetName,
        provider: entry.provider,
        details,
        attribution: attributionStatement(entry, meta),
    };
};
// The known details in display order, labelled.
export const sourceCreditDetailLines = (details) => SOURCE_CREDIT_DETAIL_ORDER.flatMap((key) => {
    const value = details[key];
    return value ? [{ key, label: SOURCE_CREDIT_DETAIL_LABELS[key], value }] : [];
});
export const formatSourceCreditDetail = (line) => `${line.label}: ${line.value}`;
